// Deposits on sold estimates in the office (BL-006 PR 2, on PR 1's server): an estimate's deposits,
// with "Take a deposit…" for cash, cheque or e-transfer (payments.record with estimateId, the amount
// suggested by estimates.depositPercent, never more than what's left of the signed total), and the
// deposits a booked job's invoice waits for or was paid by (deposits.list). Refunds are the Money tab's,
// as for any credit. A refusal says the server's words.

import { useEffect, useRef, useState } from 'react';
import { RpcError } from '@flowology/client';
import { client } from '../api.ts';
import { newId } from '../shared/ids.ts';
import { SaveButton } from '../shared/SaveButton.tsx';
import './deposits.css';
import { useDeposits } from './use-deposits.ts';
import {
  DEPOSIT_METHODS,
  DEPOSIT_NOTE,
  depositAmount,
  depositText,
  heldCents,
  suggestedCents,
  type DepositMethod,
} from './deposits-model.ts';

const say = (e: unknown) =>
  e instanceof RpcError || e instanceof Error ? e.message : 'That didn’t go through. Try again.';
const cad = (cents: number) =>
  new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(cents / 100);

/** A signed estimate's deposits, and "Take a deposit…" for those who record payments. */
export function EstimateDeposits({
  estimateId,
  estimateNumber,
  customerName,
  totalCents,
  mayTake,
  onTaken,
}: {
  estimateId: string;
  estimateNumber: string;
  customerName: string;
  totalCents: number;
  mayTake: boolean;
  onTaken?: (said: string) => void;
}) {
  const [version, setVersion] = useState(0);
  const deposits = useDeposits({ estimateId }, version);
  const [taking, setTaking] = useState(false);
  if (deposits === null) return null;
  const held = heldCents(deposits);
  const left = Math.max(0, totalCents - held);
  return (
    <div className="est-deposits" role="group" aria-label={`Deposits on estimate ${estimateNumber}`}>
      {deposits.length > 0 ? (
        <ul className="small est-deposit-lines">
          {deposits.map((d) => (
            <li key={d.creditId}>{depositText(d)}</li>
          ))}
        </ul>
      ) : (
        <span className="muted small">No deposit taken.</span>
      )}
      {mayTake && left > 0 && (
        <button
          type="button"
          className="btn quiet"
          aria-label={`Take a deposit on estimate ${estimateNumber} for ${customerName}`}
          onClick={() => setTaking(true)}
        >
          Take a deposit…
        </button>
      )}
      {taking && (
        <TakeDeposit
          estimateId={estimateId}
          estimateNumber={estimateNumber}
          customerName={customerName}
          totalCents={totalCents}
          held={held}
          onClose={() => setTaking(false)}
          onTaken={(said) => {
            setTaking(false);
            setVersion((v) => v + 1);
            onTaken?.(said);
          }}
        />
      )}
    </div>
  );
}

function TakeDeposit({
  estimateId,
  estimateNumber,
  customerName,
  totalCents,
  held,
  onClose,
  onTaken,
}: {
  estimateId: string;
  estimateNumber: string;
  customerName: string;
  totalCents: number;
  held: number;
  onClose: () => void;
  onTaken: (said: string) => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const left = Math.max(0, totalCents - held);
  const [text, setText] = useState('');
  const [method, setMethod] = useState<DepositMethod>('cheque');
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // one payment per attempt: a lost answer retried records it once
  const paymentId = useRef(newId());
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
  }, []);
  useEffect(() => {
    client
      .call<{ value: number }>('settings.get', { key: 'estimates.depositPercent' })
      .then((r) => {
        const s = suggestedCents(totalCents, Number(r.value) || 0, held);
        if (s > 0) setText((t) => t || (s / 100).toFixed(2));
      })
      .catch(() => undefined);
  }, [totalCents, held]);
  const amount = depositAmount(text, left);
  const take = async () => {
    if (!('cents' in amount) || busy) return;
    setBusy(true);
    setError(null);
    try {
      await client.call('payments.record', {
        paymentId: paymentId.current,
        method,
        amountCents: amount.cents,
        receivedAt: new Date().toISOString(),
        estimateId,
        ...(reference.trim() ? { reference: reference.trim() } : {}),
      });
      onTaken(`Deposit of ${cad(amount.cents)} taken for estimate ${estimateNumber}.`);
    } catch (e) {
      setError(say(e));
      setBusy(false);
    }
  };
  return (
    <dialog ref={ref} className="sheet ask" aria-labelledby="deposit-title" onClose={onClose}>
      <form
        method="dialog"
        onSubmit={(e) => {
          e.preventDefault();
          void take();
        }}
      >
        <h3 id="deposit-title">
          A deposit from {customerName}, estimate {estimateNumber}
        </h3>
        <p className="muted">
          {cad(left)} of the signed {cad(totalCents)} is left for deposits. {DEPOSIT_NOTE}
        </p>
        <label>
          Amount
          <input
            className="field"
            inputMode="decimal"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </label>
        <label>
          How they paid
          <select
            className="field"
            value={method}
            onChange={(e) => setMethod(e.target.value as DepositMethod)}
          >
            {DEPOSIT_METHODS.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </label>
        {method !== 'cash' && (
          <label>
            {method === 'cheque' ? 'Cheque number (optional)' : 'Reference (optional)'}
            <input
              className="field"
              maxLength={100}
              value={reference}
              onChange={(e) => setReference(e.target.value)}
            />
          </label>
        )}
        {text.trim() && 'problem' in amount && (
          <p className="error" role="alert">
            {amount.problem}
          </p>
        )}
        <div className="row end">
          <button type="button" className="btn soft" onClick={() => ref.current?.close()} disabled={busy}>
            Cancel
          </button>
          <SaveButton
            busy={busy}
            busyLabel="Taking it…"
            error={error}
            disabled={!('cents' in amount)}
            onClick={() => void take()}
          >
            {'cents' in amount ? `Take ${cad(amount.cents)}` : 'Take the deposit'}
          </SaveButton>
        </div>
      </form>
    </dialog>
  );
}

/** The deposits a job's invoice waits for, or was paid by (the job sheet of a booked estimate's work). */
export function InvoiceDeposits({ invoiceId, version }: { invoiceId: string; version: number }) {
  const deposits = useDeposits({ invoiceId }, version);
  if (!deposits?.length) return null;
  const waiting = deposits.filter((d) => d.state === 'reserved').reduce((s, d) => s + d.remainingCents, 0);
  const applied = deposits.reduce((s, d) => s + d.appliedCents, 0);
  return (
    <section className="job-sect job-deposits" aria-labelledby="job-deposits-title">
      <h3 id="job-deposits-title">Deposit</h3>
      {waiting > 0 && (
        <p className="small">Deposit paid {cad(waiting)}: taken off the invoice when the job is completed.</p>
      )}
      {applied > 0 && <p className="small">Deposit of {cad(applied)} applied to the invoice.</p>}
    </section>
  );
}
